import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const STORAGE_KEY = "chakra-ui-color-mode";

type ColorMode = "light" | "dark";

interface ColorModeContextValue {
  colorMode: ColorMode;
  toggleColorMode: () => void;
}

export function getStoredColorMode(): ColorMode {
  if (typeof document !== "undefined") {
    const html = document.documentElement;
    if (html.classList.contains("dark")) return "dark";
    if (html.classList.contains("chakra-ui-dark")) return "dark";
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

const ColorModeContext = createContext<ColorModeContextValue>({
  colorMode: "light",
  toggleColorMode: () => {},
});

export const ColorModeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [colorMode, setColorMode] = useState<ColorMode>(getStoredColorMode);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", colorMode === "dark");
    document.documentElement.classList.toggle(
      "chakra-ui-light",
      colorMode === "light",
    );
    document.documentElement.classList.toggle(
      "chakra-ui-dark",
      colorMode === "dark",
    );
    try {
      localStorage.setItem(STORAGE_KEY, colorMode);
    } catch {
      // ignore (private mode etc.)
    }
  }, [colorMode]);

  const toggleColorMode = useCallback(() => {
    setColorMode((m) => (m === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ColorModeContext.Provider value={{ colorMode, toggleColorMode }}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);
